/* DealMemoryPanel.jsx — Phase 27 per-deal memory panel.
 *
 * Mounted on the Deal Room intelligence rail. Reads
 * `/api/operational-memory/deals/{deal_id}` and surfaces:
 *  • Operational pattern match summary
 *  • Recovery likelihood band per active trigger
 *  • Median historical recovery time
 *  • Partner historical reliability per partner on this deal
 *
 * Auto-hides when no historical signal matches.
 */
import React, { useCallback, useEffect, useState } from "react";
import { BookOpen, Gauge, TrendingUp, Users } from "lucide-react";
import { api } from "../../lib/api";

const POLL_MS = 120_000;

const BAND_TONE = {
  "Likely to recover":  { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  "Probable recovery":  { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "Mixed signal":       { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "Recovery rare":      { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
  "insufficient sample":{ bg: "rgba(148,163,184,0.10)", fg: "#475569" },
};

const PARTNER_BAND_TONE = {
  "Highly responsive": { bg: "rgba(16,185,129,0.10)", fg: "#047857" },
  "Responsive":        { bg: "rgba(99,102,241,0.08)", fg: "#4338CA" },
  "Variable":          { bg: "rgba(245,158,11,0.10)", fg: "#92400E" },
  "Unresponsive":      { bg: "rgba(244,63,94,0.10)",  fg: "#9F1239" },
};

const ROLE_LABEL = {
  buyer_solicitor:  "Buyer solicitor",
  seller_solicitor: "Seller solicitor",
  mortgage_broker:  "Mortgage broker",
};

const MatchRow = ({ m }) => {
  const slug = (m.trigger_kind || "—").replace(/_/g, "-");
  const rl = m.recovery_likelihood || {};
  const sim = m.similar || {};
  const tone = BAND_TONE[rl.band] || BAND_TONE["insufficient sample"];
  return (
    <li className="py-2.5 border-t first:border-t-0"
        style={{ borderColor: "var(--gp-card-border)" }}
        data-testid={`deal-memory-match-${slug}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-[12.5px] font-semibold text-slate-900 truncate">
          {m.trigger_kind.replace(/_/g, " ")}
        </p>
        <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md"
              style={{ background: tone.bg, color: tone.fg }}
              data-testid={`deal-memory-band-${slug}`}>
          {rl.band || "—"}
        </span>
      </div>
      <p className="text-[11.5px] text-slate-700 leading-snug mt-1">
        {rl.narrative}
      </p>
      <div className="flex items-center flex-wrap gap-1.5 mt-1.5">
        {sim.sample_size ? (
          <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                style={{ background: "rgba(99,102,241,0.06)", color: "#4338CA" }}>
            <TrendingUp size={9} className="inline -mt-0.5 mr-0.5" />
            {sim.sample_size} similar case{sim.sample_size !== 1 ? "s" : ""}
          </span>
        ) : null}
        {sim.median_recovery_days != null ? (
          <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                style={{ background: "rgba(16,185,129,0.08)", color: "#047857" }}>
            <Gauge size={9} className="inline -mt-0.5 mr-0.5" />
            Median {sim.median_recovery_days}d
          </span>
        ) : null}
        {m.recommended_escalation_hours != null ? (
          <span className="text-[10.5px] px-2 py-0.5 rounded-md"
                style={{ background: "rgba(245,158,11,0.10)", color: "#92400E" }}>
            Recommend escalate by ~{m.recommended_escalation_hours}h
          </span>
        ) : null}
      </div>
    </li>
  );
};

const PartnerRow = ({ p }) => {
  const h = p.history || {};
  const tone = PARTNER_BAND_TONE[h.responsiveness_band] || PARTNER_BAND_TONE.Variable;
  const slug = (p.name || "—").replace(/\s+/g, "-");
  return (
    <li className="flex items-center justify-between py-1.5"
        data-testid={`deal-memory-partner-${slug}`}>
      <div className="min-w-0">
        <p className="text-[12px] font-semibold text-slate-900 truncate">
          {p.name}
        </p>
        <p className="text-[10.5px] text-slate-500">
          {ROLE_LABEL[p.role] || p.role}
          {h.silent_period_avg_days != null
            ? ` · avg silent ${h.silent_period_avg_days}d`
            : ""}
          {h.reliability_trend ? ` · trend ${h.reliability_trend}` : ""}
        </p>
      </div>
      <span className="text-[10.5px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md shrink-0"
            style={{ background: tone.bg, color: tone.fg }}>
        {h.responsiveness_band}
      </span>
    </li>
  );
};

export function DealMemoryPanel({ dealId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    if (!dealId) return;
    try {
      const res = await api.get(`/api/operational-memory/deals/${dealId}`);
      setData(res.data?.memory || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { refetch(); }, [refetch]);
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") refetch(); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refetch]);

  if (loading) return null;
  if (!data || !data.active) return null;

  const matches = data.matches || [];
  const partners = data.partner_history || [];

  return (
    <section className="gp-card overflow-hidden"
             data-testid="dealroom-memory-panel">
      <header className="p-4 sm:p-5">
        <div className="flex items-center gap-1.5">
          <BookOpen size={11} className="text-violet-600" strokeWidth={2.3} />
          <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-violet-700">
            Operational Memory
          </p>
        </div>
        <h2 className="text-[15px] sm:text-[16px] font-semibold text-slate-900 mt-1 leading-tight"
            data-testid="dealroom-memory-narrative">
          {data.narrative}
        </h2>
        <p className="text-[11.5px] mt-1 text-slate-500">
          Similar transactions · recovery likelihood · operational pattern match.
        </p>
      </header>
      {matches.length ? (
        <div className="px-4 sm:px-5 pb-2">
          <ul data-testid="dealroom-memory-matches">
            {matches.map((m) => <MatchRow key={m.trigger_kind} m={m} />)}
          </ul>
        </div>
      ) : null}
      {partners.length ? (
        <div className="px-4 sm:px-5 pb-4 pt-2 border-t"
             style={{ borderColor: "var(--gp-card-border)" }}>
          <div className="flex items-center gap-1.5 mb-1.5">
            <Users size={11} strokeWidth={2.3} style={{ color: "var(--gp-violet, #7C3AED)" }} />
            <p className="text-[9.5px] uppercase tracking-[0.18em] font-bold"
               style={{ color: "var(--gp-violet, #7C3AED)" }}>
              Partner historical memory
            </p>
          </div>
          <ul className="divide-y" style={{ borderColor: "var(--gp-card-border)" }}>
            {partners.map((p) => <PartnerRow key={p.name + p.role} p={p} />)}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default DealMemoryPanel;
